import React from 'react';
import { Modal, Button, Image, Badge } from 'react-bootstrap';
import { Castaway } from '../types';
import { getCastawayBio, CastawayBio } from '../data/castawayBios';
import { getTribeColor } from './TribeBadge';

interface CastawayBioModalProps {
  show: boolean;
  onHide: () => void;
  castaway: Castaway | null;
  tribes?: { name: string; color: string }[];
  onDraft?: (castaway: Castaway) => void;
  isDraftable?: boolean;
  isDrafted?: boolean;
}

export const CastawayBioModal: React.FC<CastawayBioModalProps> = ({
  show,
  onHide,
  castaway,
  tribes = [],
  onDraft,
  isDraftable = false,
  isDrafted = false
}) => {
  if (!castaway) return null;

  const bio: CastawayBio | null = getCastawayBio(castaway.name);
  const tribeColor = getTribeColor(castaway.tribe, tribes, castaway.tribeColor);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="castaway-bio-modal">
      <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title className="d-flex align-items-center gap-2">
          <span>{castaway.name}</span>
          {castaway.tribe && (
            <Badge
              style={{
                backgroundColor: tribeColor,
                color: '#fff',
                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                fontSize: '0.85rem'
              }}
            >
              {castaway.tribe}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <div className="row g-4 align-items-start">
          <div className="col-12 col-md-4 text-center">
            <div className="position-relative d-inline-block">
              <Image
                src={castaway.imageUrl}
                rounded
                referrerPolicy="no-referrer"
                style={{
                  width: '100%',
                  maxWidth: '220px',
                  height: '240px',
                  objectFit: 'cover',
                  border: `4px solid ${tribeColor}`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            </div>
            <div className="mt-3 text-muted small">
              <div><strong>Age:</strong> {castaway.age || bio?.age || 'N/A'}</div>
              <div><strong>Occupation:</strong> {castaway.occupation || bio?.occupation || 'N/A'}</div>
              {bio?.currentResidence && (
                <div><strong>Location:</strong> {bio.currentResidence}</div>
              )}
              {bio?.hometown && bio.hometown !== bio.currentResidence && (
                <div><strong>Hometown:</strong> {bio.hometown}</div>
              )}
            </div>
          </div>

          <div className="col-12 col-md-8">
            {bio?.highlights && (
              <div className="p-3 mb-3 rounded" style={{ backgroundColor: 'rgba(13, 110, 253, 0.08)', borderLeft: '4px solid #0d6efd' }}>
                <h6 className="fw-bold mb-1 text-primary">Contestant Spotlight</h6>
                <p className="mb-0 text-dark" style={{ fontSize: '0.95rem' }}>{bio.highlights}</p>
              </div>
            )}

            {bio?.bioQuotes && bio.bioQuotes.length > 0 ? (
              <div className="mb-3">
                <h6 className="fw-bold text-secondary mb-2">In Their Own Words & Strategy</h6>
                {bio.bioQuotes.map((quote, idx) => (
                  <blockquote
                    key={idx}
                    className="blockquote ps-3 py-1 mb-2 border-start border-3 border-warning"
                    style={{ fontSize: '0.92rem', fontStyle: 'italic', color: '#334155' }}
                  >
                    “{quote.replace(/^“|”$/g, '')}”
                  </blockquote>
                ))}
              </div>
            ) : (
              <p className="text-muted">No extended biography quotes available for this castaway.</p>
            )}

            {bio?.sourceUrl && (
              <div className="mt-3 small text-muted">
                Source: <a href={bio.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-underline">People Magazine Season 51 Cast Reveal</a>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between align-items-center">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        {isDraftable && onDraft && (
          <Button
            variant={isDrafted ? "secondary" : "success"}
            disabled={isDrafted}
            onClick={() => {
              onDraft(castaway);
              onHide();
            }}
          >
            {isDrafted ? "Already in Your Roster" : `Draft ${castaway.name}`}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CastawayBioModal;
